import DateHelpers from './dateHelpers';
import type { InspectionSyncResult } from '../types/inspection-sync';

export const formatCount = (n: number, noun: string) =>
  `${n.toLocaleString()} ${n === 1 ? noun : `${noun}s`}`;

export const formatSyncTime = (time: Date | string): string =>
  DateHelpers.toStringX(typeof time === 'string' ? time : time.toISOString());

export function formatSyncCounts(result: InspectionSyncResult): string {
  const other =
    result.otherRecords > 0
      ? `, ${formatCount(result.otherRecords, 'other record')}`
      : '';
  return `${formatCount(result.assets, 'asset')}, ${formatCount(result.sites, 'site')}, ${formatCount(result.inspections, 'inspection')}${other}`;
}

/** The message shown after "Refresh data", built from what the sync actually received. */
export function describeSyncResult(result: InspectionSyncResult): {
  message: string;
  isWarning: boolean;
} {
  const counts = formatSyncCounts(result);
  const skipped = result.skippedAssets.length;
  const skippedNote =
    skipped > 0
      ? ` ${formatCount(skipped, 'asset')} could not be added - see Support.`
      : '';

  if (result.isFullSync || !result.syncedSince) {
    return {
      message: `Full sync complete: ${counts}.${skippedNote}`,
      isWarning: skipped > 0,
    };
  }

  const received =
    result.assets +
    result.sites +
    result.inspections +
    result.otherRecords +
    skipped;
  if (received === 0) {
    return {
      message: `No changes since ${formatSyncTime(result.syncedSince)}.`,
      isWarning: false,
    };
  }

  return {
    message: `Updated: ${counts}.${skippedNote}`,
    isWarning: skipped > 0,
  };
}

export function describeSyncFailure(lastSyncDate: Date | null): string {
  return lastSyncDate
    ? `Sync failed. The data on this device is from the last successful sync at ${formatSyncTime(lastSyncDate)}.`
    : 'Sync failed. No sync has completed on this device yet.';
}
